<template>
  <Modal
    :modal-active="modelValue"
    has-header-border
    has-footer-border
    max-width="max-w-[560px]"
    @close="close"
  >
    <template #header>
      <div class="flex flex-col gap-1 text-left">
        <h2 class="text-xl font-bold text-textPrimary">
          {{ items.length > 1 ? `Aprovar ${items.length} documentos` : 'Aprovar documento' }}
        </h2>
        <p class="text-sm text-[#52526f]">
          Documento aprovado vale para todas as empresas em que o médico atua.
        </p>
      </div>
    </template>

    <div class="flex flex-col gap-4 text-left" data-testid="approve-document-modal">
      <div
        v-for="item in items"
        :key="item.versionId"
        class="flex flex-col gap-3 rounded-lg border border-[#e8e8ed] p-3"
        :data-testid="`approve-document-item-${item.versionId}`"
      >
        <div>
          <p class="text-sm font-semibold text-textPrimary">{{ item.documentName }}</p>
          <p class="text-xs text-[#52526f]">
            {{ item.professionalName
            }}<template v-if="item.crmLabel"> · {{ item.crmLabel }}</template>
          </p>
        </div>
        <template v-if="item.hasExpiry">
          <InputDate
            label="Data de validade"
            required
            mask="##/##/####"
            width="100%"
            :value="form[item.versionId]?.expiresAt ?? ''"
            :is-error="!!errors[item.versionId]"
            :error-message="errors[item.versionId] ?? ''"
            :data-testid="`approve-document-expiry-${item.versionId}-input`"
            @update:model-value="(value: string) => setField(item.versionId, 'expiresAt', value)"
          />
          <p class="-mt-2 text-[11px] text-[#7f7f8e]">
            <!-- O espaço vai na interpolação: o compilador descarta o espaço final do <template>. -->
            {{ item.description ? `${item.description} ` : '' }}O médico é avisado em D-14, D-3 e
            D0; o documento vira Pendente em D+1.
          </p>
        </template>
        <p v-else class="text-[11px] text-[#7f7f8e]">Este tipo não tem validade.</p>
        <!-- Limite do backend (`MaxLength(100)` no DTO de aprovação). -->
        <Input
          v-if="item.acceptsAuthenticityCode"
          label="Código de autenticidade (opcional)"
          width="100%"
          max="100"
          :value="form[item.versionId]?.authenticityCode ?? ''"
          :data-testid="`approve-document-code-${item.versionId}-input`"
          @update:model-value="
            (value: string) => setField(item.versionId, 'authenticityCode', value)
          "
        />
      </div>
    </div>

    <template #footer>
      <div class="flex gap-4 w-full">
        <div class="flex-1">
          <OutlineButton
            text="Cancelar"
            width="100%"
            height="48px"
            data-testid="approve-document-cancel"
            @click="close"
          />
        </div>
        <div class="flex-1">
          <FilledButton
            :text="items.length > 1 ? `Aprovar ${items.length}` : 'Aprovar documento'"
            width="100%"
            height="48px"
            :loading="isSaving"
            :disabled="isSaving"
            data-testid="approve-document-confirm"
            @click="confirm"
          />
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

import Modal from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import InputDate from '@/components/designSystem/Inputs/InputDate.vue'
import Input from '@/components/designSystem/Inputs/Input.vue'
import type { ApproveTarget } from '@/pinia-store/useDocumentReviewStore'

import { buildApprovePayload, todayInSaoPaulo, validateApproveForm } from '../model'
import type { ApproveFormItem } from '../model'

export interface ApproveModalItem {
  versionId: number
  userId: number
  documentName: string
  professionalName: string
  crmLabel: string | null
  hasExpiry: boolean
  acceptsAuthenticityCode: boolean
  /** Texto "Como conferir" do catálogo, repetido junto da validade. */
  description: string | null
}

const props = defineProps<{ modelValue: boolean; items: ApproveModalItem[]; isSaving: boolean }>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  confirm: [targets: ApproveTarget[]]
}>()

type FieldValues = Pick<ApproveFormItem, 'expiresAt' | 'authenticityCode'>
const form = ref<Record<number, FieldValues>>({})
const errors = ref<Record<number, string>>({})

// Abrir (ou trocar os itens) começa um formulário limpo: nada da decisão anterior vaza.
watch(
  () => [props.modelValue, props.items.map((item) => item.versionId).join(',')],
  () => {
    form.value = Object.fromEntries(
      props.items.map((item) => [item.versionId, { expiresAt: '', authenticityCode: '' }])
    )
    errors.value = {}
  },
  { immediate: true }
)

function setField(versionId: number, field: keyof FieldValues, value: string) {
  form.value = { ...form.value, [versionId]: { ...form.value[versionId], [field]: value } }
  if (field === 'expiresAt' && errors.value[versionId]) {
    const next = { ...errors.value }
    delete next[versionId]
    errors.value = next
  }
}

const toFormItem = (item: ApproveModalItem): ApproveFormItem => ({
  versionId: item.versionId,
  hasExpiry: item.hasExpiry,
  acceptsAuthenticityCode: item.acceptsAuthenticityCode,
  expiresAt: form.value[item.versionId]?.expiresAt ?? '',
  authenticityCode: form.value[item.versionId]?.authenticityCode ?? ''
})

function confirm() {
  const formItems = props.items.map(toFormItem)
  errors.value = validateApproveForm(formItems, todayInSaoPaulo())
  if (Object.keys(errors.value).length) return
  emit(
    'confirm',
    props.items.map((item, index) => ({
      versionId: item.versionId,
      userId: item.userId,
      payload: buildApprovePayload(formItems[index])
    }))
  )
}

function close() {
  emit('update:modelValue', false)
}
</script>
